import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Phase 37 — Reusable `<PageSEO />` component.
 *
 * Emits per-page `<title>`, `<meta name="description">`, canonical link,
 * Open Graph + Twitter cards, and (optionally) a JSON-LD structured-data
 * script. Every prop is optional; the site-wide fallbacks live in
 * `public/index.html` so any page that forgets to add a `<PageSEO />`
 * still ships reasonable SEO defaults.
 *
 * Consumers should keep `title` under 60 chars and `description` under
 * 160 chars for optimal Google/Bing rendering.
 */
export default function PageSEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} · MercaGo` : "MercaGo · Envío de productos halal a los campamentos saharauis";
  const finalDesc = description ||
    "Compra productos halal desde España y entrégalos directamente a tu familia en los campamentos saharauis de Tindouf. Wilayas: Smara, El Aaiún, Auserd, Dajla, Bojador, 27 de Febrero, Rabuni.";
  const finalImage = ogImage || "https://mercago.one/og-image.png";
  const url = canonical || (typeof window !== "undefined" ? window.location.href : "https://mercago.one/");

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="MercaGo" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:locale" content="es_ES" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImage} />
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
